<script lang="ts">
	import RouteFeaturesStructure from '$lib/components/city/features/route/RouteFeaturesStructure.svelte';
	import HeroNeutral from '$lib/components/ui/HeroNeutral.svelte';
	import { Difficulty, DIFFICULTY_METAS } from '$lib/features/_other/difficulty.enum';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import { CITY_FEATURE_TYPE_COLOURS, CityFeatureType } from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { CoverImage } from '$lib/features/image/imageDetails.type';
	import type { ScenicBikeRidePreview } from '$lib/features/scenicBikeRide/scenicBikeRide.type';
	import { SCENIC_BIKE_RIDE_CATEGORY_LABELS } from '$lib/features/scenicBikeRide/scenicBikeRideCategory.type';
	import { FULL_SITE_URL } from '$lib/statics';
	import { getTopFrequentValues } from '$lib/utils/general.util';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { UrlUtil } from '$lib/utils/url.util';
	import type { Output } from './+page.server';

	const featureType: CityFeatureType = CityFeatureType.SCENIC_BIKE_RIDE;

	const { data }: { data: { output: Output } } = $props();

	const output = $derived<Output>(data.output);
	const location = $derived<PlaceLocation>(output.location);
	const meta = $derived.by(() => {
		const firstAvailableImage: CoverImage | undefined = output.items.flatMap((ride) => ride.stops.flatMap((s) => s.images)).at(0);
		const itemsLength = output.items.length;
		const routesWording = itemsLength === 1 ? 'route' : 'routes';
		const descriptionNames =
			itemsLength === 1
				? output.items[0].feature.englishName
				: itemsLength === 2
					? `from ${output.items.map((item) => item.feature.englishName).join(' to ')}`
					: `from ${output.items
							.slice(0, 2)
							.map((item) => item.feature.englishName)
							.join(' to ')} & more`;
		const topCategories = getTopFrequentValues(
			output.items.map((i) => i.categories),
			2
		).map((e) => SCENIC_BIKE_RIDE_CATEGORY_LABELS[e]);
		return {
			title: `${location.city.label} Cycling Routes: From ${topCategories.join(' to ')} Trails`,
			description: `Explore ${location.city.label} by bike with ${output.items.length} curated cycling ${routesWording}, ${descriptionNames}. Choose from ${getDifficultyRange(output.items)} rides.`,
			canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toCityFeatures({ location, type: featureType }).href}`,
			imageUrl: firstAvailableImage ? UrlUtil.getFullImageUrl(firstAvailableImage) : undefined
		};
	});
	const combinedJsonLd = $derived(data.output.jsonLdBreadcrumbs);

	function getDifficultyRange(items: ScenicBikeRidePreview[]): string {
		if (!items.length) return 'easy to challenging';
		const diffs = new Set(items.map((i) => i.difficulty));
		if (diffs.has(Difficulty.EASY) && diffs.has(Difficulty.MODERATE)) return 'easy to moderate';
		if (diffs.has(Difficulty.EASY) && diffs.has(Difficulty.CHALLENGING)) return 'easy to challenging';
		if (diffs.has(Difficulty.MODERATE) && diffs.has(Difficulty.CHALLENGING)) return 'moderate to challenging';
		if (diffs.has(Difficulty.MODERATE)) return 'moderate';
		if (diffs.has(Difficulty.CHALLENGING)) return 'challenging';
		return 'easy';
	}
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	<meta property="og:image" content={meta.imageUrl} />
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:url" content={meta.canonicalUrl} />
	<meta property="twitter:image" content={meta.imageUrl} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<RouteFeaturesStructure
	items={output.items}
	{location}
	language={output.countryLanguage}
	keepExploring={output.keepExploring}
	onExtraStats={(featureId: string) => {
		const ride = output.items.find((r) => r.feature.id === featureId)!;
		const meta = DIFFICULTY_METAS[ride.difficulty];
		return [
			{
				title: 'Intensity',
				Icon: meta.Icon,
				text: meta.label,
				textColour: meta.colour
			}
		];
	}}
>
	{#snippet HeaderSnippet()}
		<HeroNeutral
			title={`Best Cycling Routes in ${location.city.label}`}
			description={`Discover the city on two wheels. We've mapped out ${output.items.length} beautiful cycling routes suited for every
			skill level, with a diverse set of views along the way.`}
			breadcrumbs={data.output.breadcrumbs}
			decorations={{
				hexColour: CITY_FEATURE_TYPE_COLOURS[featureType]
			}}
		/>
	{/snippet}
</RouteFeaturesStructure>
